<script setup>
/**
 * ONE QUESTION, TWO LANGUAGES, ONE BOX. THE DESIGN, §6.2.
 *
 * ── THE PROBLEM THIS SOLVES, MEASURED ─────────────────────────────────────────────────
 * The engine has carried a per-language customer question the whole time and the UI
 * exposed it nowhere — `grep questionEn` across this fork returned nothing until this
 * file. So asking the sidecar for an Arabic card today gets no card at all, every step
 * refused with `no_question_in_language`, and an Arabic customer can never be shown one.
 *
 * The obvious fix — two equal boxes — doubles the height of the one screen an owner meets
 * most often, on a form that is already the densest thing in the product. So: ONE box,
 * and a GHOST LINE under it carrying the other language. You are never in the state of
 * not knowing whether the other side exists, or whether it is the right words; and the
 * screen grows by about a sixth rather than doubling.
 *
 * ── NEITHER LANGUAGE IS SECOND-CLASS, AND THAT IS A DATA RULE, NOT A COURTESY ─────────
 * `schema.ts` holds `questionEn` and `questionAr` as the same shape, both optional, blank
 * allowed on either — an owner who writes the Arabic and not the English must be able to
 * save. So this control is SYMMETRIC: the box opens on the language the dashboard itself
 * is in, `Edit` on the ghost line swaps which one is in the box, and the two write to two
 * emits of equal standing. What is quieter on screen is not quieter in the data.
 *
 * ── EVERY BILINGUAL FIELD, ONE CONTROL (P-7.1) ───────────────────────────────────────
 * A question (`ask`, `choose` and, since P-10.24, `choose_live`), an option label, a message
 * in the owner's exact words, the form's button, a menu's rows, message and button, and the
 * handover line. Each passes its own `maxLength` and translate `kind`.
 *
 * ── TYPE ONCE (P-7.1) ─────────────────────────────────────────────────────────────────
 * Whatever script the owner types becomes that field's main language: Arabic letters typed
 * into an empty pair are the ARABIC words, whichever box was open. A tie keeps the box's
 * language. The note under the box says where they were saved, with a Change that moves them.
 * The other language is offered as "Suggest · {credits}": priced, confirmed in place, charged
 * (P-5.12), and written only when she presses Use this.
 *
 * On a ONE-language account (`languages` `ar` or `en`) the box is that language only: no
 * ghost line, no moving, no Suggest. Whatever she types is written to that field.
 *
 * ── NO OVERLAY, DELIBERATELY ──────────────────────────────────────────────────────────
 * The swap, the note, the suggestion and the price confirmation all happen in place.
 * `Dialog`, `Popover` and `SidePanel` all reach the Vuex store through
 * `TeleportWithDirection` and throw inside a render effect AFTER a unit test has resolved,
 * so they can only ever fail the exit code, which is how the image build died on both
 * environments. `AiConfirmInline.vue` is the confirm, drawn here.
 */
import { computed, ref, watch } from 'vue';

import Button from 'dashboard/components-next/button/Button.vue';

import AiConfirmInline from '../AiConfirmInline.vue';
import WasloInput from '../WasloInput.vue';
import WasloTextArea from '../WasloTextArea.vue';
import { useWasloI18n } from '../../i18n.js';
import { useWasloAccountId } from '../../composables/useWasloAccountId.js';
import { useAiConfirm } from '../../composables/useAiConfirm.js';
import { wasloApi, withConfirm } from '../../api/client.js';
import {
  MAX_QUESTION,
  flatten,
  otherLang,
  scriptOf,
} from '../../helpers/task-arabic.js';
import { isOneLanguage } from '../../helpers/missing-language.js';

const props = defineProps({
  /** The words as an English-reading customer sees them. Blank counts as absent. */
  en: { type: String, default: '' },
  /** The words as an Arabic-reading customer sees them. Blank counts as absent. */
  ar: { type: String, default: '' },
  label: { type: String, default: '' },
  hint: { type: String, default: '' },
  placeholderEn: { type: String, default: '' },
  placeholderAr: { type: String, default: '' },
  /** Validation from the parent, shown under whichever box is open. */
  message: { type: String, default: '' },
  messageType: { type: String, default: 'error' },
  disabled: { type: Boolean, default: false },
  /** The field's own cap (`schema.ts`, `menus/limits.ts`). */
  maxLength: { type: Number, default: MAX_QUESTION },
  /**
   * Which language starts in the box. Empty (the default) means "the one this dashboard is
   * in", which is what makes writing Arabic first as ordinary as writing English first.
   */
  writing: { type: String, default: '' },
  /** A box for a message that may hold line breaks (a `tell`, a menu message, a handover). */
  multiline: { type: Boolean, default: false },
  minHeight: { type: String, default: '4rem' },
  /** The translate kind (`assist/translate-contract.ts`). Empty: no Suggest. */
  kind: { type: String, default: '' },
  /** The account's "Your customers write in": `ar`, `en` or `both`. */
  languages: { type: String, default: 'both' },
  /**
   * Always show "{count} of {max}" under the box, not only past the cap: a menu row's limit
   * moves (20 as a button, 24 in a list), so it is shown, not only enforced.
   */
  counter: { type: Boolean, default: false },
});

const emit = defineEmits(['update:en', 'update:ar', 'update:pair', 'swap']);

const { t, locale } = useWasloI18n();

const langOf = value => (String(value).split(/[-_]/)[0] === 'ar' ? 'ar' : 'en');

const oneLanguage = computed(() => isOneLanguage(props.languages));

const startsOn = () => {
  if (isOneLanguage(props.languages)) return props.languages;
  if (props.writing === 'ar' || props.writing === 'en') return props.writing;
  return langOf(locale.value);
};

const open = ref(startsOn());

// A parent that pins the side (the sweep screen writes one language) or an account that
// changes to one language must be able to change the box without a remount.
watch(
  () => [props.writing, props.languages],
  () => {
    open.value = startsOn();
  }
);

const other = computed(() => otherLang(open.value));

const valueOf = lang => (lang === 'ar' ? props.ar : props.en);

const current = computed(() => valueOf(open.value));

/** How long the words are, as the field will hold them. */
const measure = text =>
  props.multiline ? String(text ?? '').trim().length : flatten(text).length;

const ghost = computed(() => flatten(valueOf(other.value)));

const tagOf = lang => t(`WASLO.COMMON.LANG_TAG.${lang.toUpperCase()}`);
const nameOf = lang => t(`WASLO.COMMON.LANG_NAME.${lang.toUpperCase()}`);

const placeholder = computed(() =>
  open.value === 'ar' ? props.placeholderAr : props.placeholderEn
);

const overLimit = computed(() => measure(current.value) > props.maxLength);

const localMessage = computed(() => {
  if (props.message) return props.message;
  return overLimit.value || props.counter
    ? t('WASLO.TASKS.BILINGUAL.TOO_LONG', {
        count: measure(current.value),
        max: props.maxLength,
      })
    : '';
});

const messageTone = computed(() => {
  if (props.message) return props.messageType;
  return overLimit.value ? 'error' : 'info';
});

// Spelt out rather than `emit(open === 'ar' ? … : …)`: the two emits are declared
// separately, and a dynamic event name hides them from every tool that reads the
// declaration, including the lint that tells a caller which events exist.
const emitFor = (lang, value) => {
  if (lang === 'ar') emit('update:ar', value);
  else emit('update:en', value);
};

// ── type once: the script decides the field ─────────────────────────────────────────
/** The language typed words were moved to, for the note; '' when nothing moved. */
const moved = ref('');
/** After a Change, the owner has said which field this is: the script no longer decides. */
const pinned = ref(false);

const onInput = value => {
  if (oneLanguage.value) {
    emitFor(open.value, value);
    return;
  }
  const script = scriptOf(value);
  const movable =
    !pinned.value &&
    script !== null &&
    script !== open.value &&
    flatten(valueOf(script)) === '';
  if (movable) {
    // ONE event for both fields: two emits in a row would each be applied to the same
    // stale row by a parent that derives the next row from its props.
    open.value = script;
    moved.value = script;
    emit('update:pair', {
      en: script === 'en' ? value : '',
      ar: script === 'ar' ? value : '',
      primary: script,
    });
    return;
  }
  emitFor(open.value, value);
};

/** Put the words in the other field (and whatever was there in this one). */
const change = () => {
  const from = open.value;
  const to = other.value;
  const here = valueOf(from);
  const there = valueOf(to);
  pinned.value = true;
  moved.value = '';
  open.value = to;
  emit('update:pair', {
    en: to === 'en' ? here : there,
    ar: to === 'ar' ? here : there,
    primary: to,
  });
};

const looksOther = computed(() => {
  if (oneLanguage.value) return null;
  const script = scriptOf(current.value);
  return script !== null && script !== open.value ? script : null;
});

const note = computed(() => {
  if (looksOther.value !== null) {
    return t('WASLO.TASKS.BILINGUAL.LOOKS_LIKE', {
      language: nameOf(looksOther.value),
      other: nameOf(open.value),
    });
  }
  if (moved.value !== '' && moved.value === open.value) {
    return t('WASLO.TASKS.BILINGUAL.SAVED_AS', {
      language: nameOf(open.value),
    });
  }
  return '';
});

const swap = () => {
  moved.value = '';
  open.value = other.value;
  emit('swap', open.value);
};

// ── Suggest the other language (P-7.1, priced and confirmed: P-5.12) ─────────────────
const accountId = useWasloAccountId();
const suggestCharge = useAiConfirm({ accountId, op: 'suggest_translation' });
const { receiptText } = suggestCharge;

const proposal = ref('');
const proposalTo = ref('en');
const isSuggesting = ref(false);
const suggestError = ref('');

const canSuggest = computed(
  () =>
    !oneLanguage.value &&
    props.kind !== '' &&
    !props.disabled &&
    measure(current.value) > 0 &&
    ghost.value === '' &&
    proposal.value === ''
);

const suggestLabel = computed(() =>
  suggestCharge.label(t('WASLO.TASKS.BILINGUAL.SUGGEST'))
);

/** 402 says top up; 422 is the sidecar's own sentence with both numbers; the rest, one line. */
const failureText = error => {
  if (error?.status === 402) return t('WASLO.TEST.REFUSED.OUT_OF_CREDITS');
  if (error?.status === 422 && error.message) return error.message;
  return t('WASLO.TASKS.BILINGUAL.SUGGEST_FAILED');
};

const suggest = async () => {
  if (isSuggesting.value || !canSuggest.value) return;
  const from = open.value;
  const to = other.value;
  const text = props.multiline ? current.value.trim() : flatten(current.value);
  isSuggesting.value = true;
  suggestError.value = '';
  try {
    const outcome = await suggestCharge.confirmAndRun({
      run: credits =>
        wasloApi.translate(
          accountId.value,
          { kind: props.kind, from, to, text },
          undefined,
          withConfirm(credits)
        ),
    });
    // Cancel costs nothing and changes nothing.
    if (outcome.cancelled) return;
    proposal.value = outcome.result.text;
    proposalTo.value = to;
  } catch (error) {
    suggestError.value = failureText(error);
  } finally {
    isSuggesting.value = false;
  }
};

const useProposal = () => {
  const text = proposal.value;
  proposal.value = '';
  if (text !== '') emitFor(proposalTo.value, text);
};

const discardProposal = () => {
  proposal.value = '';
};

defineExpose({ open });
</script>

<template>
  <div
    class="flex flex-col min-w-0"
    data-waslo-bilingual
    :data-waslo-bilingual-open="open"
  >
    <WasloTextArea
      v-if="multiline"
      :model-value="current"
      :label="label"
      :hint="hint"
      :placeholder="placeholder"
      :disabled="disabled"
      :message="localMessage"
      :message-type="messageTone"
      :min-height="minHeight"
      :data-waslo-bilingual-box="open"
      @update:model-value="onInput"
    />
    <WasloInput
      v-else
      :model-value="current"
      :label="label"
      :hint="hint"
      :placeholder="placeholder"
      :disabled="disabled"
      :message="localMessage"
      :message-type="messageTone"
      :data-waslo-bilingual-box="open"
      @update:model-value="onInput"
    />

    <p
      v-if="note"
      class="flex flex-wrap items-baseline mt-1 mb-0 text-xs gap-x-2 text-n-slate-11"
      data-waslo-bilingual-note
    >
      <span dir="auto">{{ note }}</span>
      <Button
        :label="t('WASLO.TASKS.BILINGUAL.CHANGE')"
        size="sm"
        variant="link"
        color="slate"
        :disabled="disabled"
        data-waslo-bilingual-change
        @click="change"
      />
    </p>

    <!--
      THE GHOST LINE. One line, at the smallest reading size, under the box it belongs to:
      not a second field, not a tab, not a chevron. Absent on a one-language account, which
      has no other language to show.
    -->
    <div
      v-if="!oneLanguage"
      class="flex flex-wrap items-baseline mt-2 text-xs gap-x-2 gap-y-1"
      data-waslo-bilingual-ghost
      :data-waslo-bilingual-other="other"
    >
      <template v-if="ghost">
        <span class="flex-shrink-0 font-medium text-n-slate-10" dir="auto">
          {{ tagOf(other) }}
        </span>
        <span
          dir="auto"
          class="min-w-0 truncate text-n-slate-11"
          :title="ghost"
          data-waslo-bilingual-ghost-text
        >
          {{ ghost }}
        </span>
        <span
          class="flex-shrink-0 size-3.5 i-lucide-check text-n-teal-10"
          :aria-label="
            t('WASLO.TASKS.BILINGUAL.WRITTEN_ARIA', { language: nameOf(other) })
          "
          role="img"
        />
      </template>
      <!--
        `dir="auto"` because this sentence names a language inside the owner's own
        language, so an Arabic dashboard and an English one put its strong characters in
        opposite places. Resolved per sentence, it is right for both.
      -->
      <span
        v-else
        dir="auto"
        class="min-w-0 text-n-slate-11"
        data-waslo-bilingual-ghost-missing
      >
        {{ t('WASLO.TASKS.BILINGUAL.MISSING', { language: nameOf(other) }) }}
      </span>

      <Button
        v-if="canSuggest"
        :label="suggestLabel"
        icon="i-lucide-languages"
        size="sm"
        variant="link"
        color="slate"
        class="flex-shrink-0"
        :is-loading="isSuggesting"
        data-waslo-bilingual-suggest
        @click="suggest"
      />
      <Button
        :label="
          ghost
            ? t('WASLO.TASKS.BILINGUAL.EDIT')
            : t('WASLO.TASKS.BILINGUAL.WRITE')
        "
        size="sm"
        variant="link"
        color="slate"
        class="flex-shrink-0"
        :disabled="disabled"
        data-waslo-bilingual-swap
        @click="swap"
      />
    </div>

    <AiConfirmInline :controller="suggestCharge" class="mt-2" />

    <p
      v-if="suggestError"
      class="mt-2 mb-0 text-xs text-n-ruby-11"
      data-waslo-bilingual-suggest-error
    >
      {{ suggestError }}
    </p>
    <p
      v-if="receiptText"
      class="mt-1 mb-0 text-xs text-n-slate-11"
      data-waslo-ai-receipt
    >
      {{ receiptText }}
    </p>

    <!-- Proposed, never applied: her box is untouched until she presses Use this. -->
    <section
      v-if="proposal"
      class="flex flex-col gap-2 p-3 mt-2 rounded-lg outline outline-1 outline-n-weak bg-n-solid-1"
      data-waslo-bilingual-proposal
    >
      <p class="mb-0 text-xs font-medium text-n-slate-11">
        {{ t('WASLO.TASKS.ARABIC.PROPOSED') }} · {{ tagOf(proposalTo) }}
      </p>
      <p
        dir="auto"
        class="mb-0 text-sm whitespace-pre-wrap text-n-slate-12"
        data-waslo-bilingual-proposal-text
      >
        {{ proposal }}
      </p>
      <div class="flex gap-2">
        <Button
          :label="t('WASLO.TASKS.ARABIC.ACCEPT')"
          size="sm"
          data-waslo-bilingual-accept
          @click="useProposal"
        />
        <Button
          :label="t('WASLO.TASKS.ARABIC.DISCARD')"
          size="sm"
          variant="faded"
          color="slate"
          data-waslo-bilingual-discard
          @click="discardProposal"
        />
      </div>
    </section>
  </div>
</template>
